/**
 * Contenedor de inyeccion de dependencias.
 *
 * Une las capas: infraestructura implementa los puertos, los casos de uso
 * dependen solo de las interfaces del dominio.
 */

import { AxiosHttpClient } from '@infrastructure/http';
import { LocalStorageAdapter } from '@infrastructure/storage';
import {
  AccessRepository,
  ApplicationRepository,
  RoleRepository,
  UserRepository,
} from '@infrastructure/repositories';
import { OidcAuthService } from '@infrastructure/services';
import {
  AssignRoleUseCase,
  GetMyRolesUseCase,
  GetUserRolesUseCase,
  InviteUserUseCase,
  ListApplicationsUseCase,
  ListRolesUseCase,
  ListUsersUseCase,
  ResetUserPasswordUseCase,
  RevokeRoleUseCase,
  SaveApplicationUseCase,
  SaveRoleUseCase,
  ToggleApplicationStatusUseCase,
  ToggleRoleStatusUseCase,
  ToggleUserStatusUseCase,
} from '@application/use-cases';
import type { IHttpClient, IStorage } from '@application/ports';
import type {
  IAccessRepository,
  IApplicationRepository,
  IRoleRepository,
  IUserRepository,
} from '@domain/repositories';
import type { IAuthService } from '@domain/services';

interface ServiceRegistry {
  // Puertos de infraestructura
  IHttpClient: IHttpClient;
  IStorage: IStorage;

  // Repositorios
  IApplicationRepository: IApplicationRepository;
  IRoleRepository: IRoleRepository;
  IUserRepository: IUserRepository;
  IAccessRepository: IAccessRepository;

  // Servicios de dominio
  IAuthService: IAuthService;

  // Casos de uso
  ListApplicationsUseCase: ListApplicationsUseCase;
  SaveApplicationUseCase: SaveApplicationUseCase;
  ToggleApplicationStatusUseCase: ToggleApplicationStatusUseCase;
  ListRolesUseCase: ListRolesUseCase;
  SaveRoleUseCase: SaveRoleUseCase;
  ToggleRoleStatusUseCase: ToggleRoleStatusUseCase;
  ListUsersUseCase: ListUsersUseCase;
  InviteUserUseCase: InviteUserUseCase;
  ToggleUserStatusUseCase: ToggleUserStatusUseCase;
  ResetUserPasswordUseCase: ResetUserPasswordUseCase;
  GetUserRolesUseCase: GetUserRolesUseCase;
  GetMyRolesUseCase: GetMyRolesUseCase;
  AssignRoleUseCase: AssignRoleUseCase;
  RevokeRoleUseCase: RevokeRoleUseCase;
}

class DIContainer {
  private static instance: DIContainer;
  private services: Map<keyof ServiceRegistry, unknown> = new Map();

  private constructor() {
    this.registerServices();
  }

  public static getInstance(): DIContainer {
    if (!DIContainer.instance) {
      DIContainer.instance = new DIContainer();
    }
    return DIContainer.instance;
  }

  private registerServices(): void {
    const authService = new OidcAuthService();
    this.services.set('IAuthService', authService);

    // El cliente HTTP toma el token del servicio de autenticacion en cada
    // request, para no quedarse con uno vencido.
    this.services.set('IHttpClient', new AxiosHttpClient(() => authService.getAccessToken()));
    this.services.set('IStorage', new LocalStorageAdapter());

    const httpClient = this.get<IHttpClient>('IHttpClient');

    this.services.set('IApplicationRepository', new ApplicationRepository(httpClient));
    this.services.set('IRoleRepository', new RoleRepository(httpClient));
    this.services.set('IUserRepository', new UserRepository(httpClient));
    this.services.set('IAccessRepository', new AccessRepository(httpClient));

    const applicationRepository = this.get<IApplicationRepository>('IApplicationRepository');
    const roleRepository = this.get<IRoleRepository>('IRoleRepository');
    const userRepository = this.get<IUserRepository>('IUserRepository');
    const accessRepository = this.get<IAccessRepository>('IAccessRepository');

    this.services.set('ListApplicationsUseCase', new ListApplicationsUseCase(applicationRepository));
    this.services.set('SaveApplicationUseCase', new SaveApplicationUseCase(applicationRepository));
    this.services.set(
      'ToggleApplicationStatusUseCase',
      new ToggleApplicationStatusUseCase(applicationRepository)
    );

    this.services.set('ListRolesUseCase', new ListRolesUseCase(roleRepository));
    this.services.set('SaveRoleUseCase', new SaveRoleUseCase(roleRepository));
    this.services.set('ToggleRoleStatusUseCase', new ToggleRoleStatusUseCase(roleRepository));

    this.services.set('ListUsersUseCase', new ListUsersUseCase(userRepository));
    this.services.set('InviteUserUseCase', new InviteUserUseCase(userRepository));
    this.services.set('ToggleUserStatusUseCase', new ToggleUserStatusUseCase(userRepository));
    this.services.set('ResetUserPasswordUseCase', new ResetUserPasswordUseCase(userRepository));

    this.services.set('GetUserRolesUseCase', new GetUserRolesUseCase(accessRepository));
    this.services.set('GetMyRolesUseCase', new GetMyRolesUseCase(accessRepository));
    this.services.set('AssignRoleUseCase', new AssignRoleUseCase(accessRepository));
    this.services.set('RevokeRoleUseCase', new RevokeRoleUseCase(accessRepository));
  }

  public get<T>(key: keyof ServiceRegistry): T {
    const service = this.services.get(key);
    if (!service) {
      throw new Error(`Service "${String(key)}" not found in DI container`);
    }
    return service as T;
  }

  public register<K extends keyof ServiceRegistry>(key: K, instance: ServiceRegistry[K]): void {
    this.services.set(key, instance);
  }

  public has(key: keyof ServiceRegistry): boolean {
    return this.services.has(key);
  }

  public clear(): void {
    this.services.clear();
  }

  public reset(): void {
    this.clear();
    this.registerServices();
  }
}

export const container = DIContainer.getInstance();

export { DIContainer };
